// Case summary query.
//
// Round 3: the visible summary shrank to compact metadata (dispute-type tag,
// contractor, total paid). The narrative + at-stake fields and the
// regenerate button were cut. Stale-then-revalidate: we render whatever
// the server has, and the server silently regenerates when the event count
// changes. No client-driven regeneration.
//
// Cost discipline (CLAUDE.md hard rule #3): page views always hit the cache.
// The only LLM cost is on event-count change.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { apiFetch } from './api';
import { useMe } from './auth';
import { caseQueryKey, casesQueryKey } from './cases';

export type CaseSummary = {
  id: string;
  case_id: string;
  /** Reserved for the M3 case-pack PDF; not rendered. */
  narrative: string | null;
  dispute_type: 'defect' | 'abandonment' | 'payment' | 'mixed' | 'other';
  key_facts: {
    whoIsContractor?: string;
    totalPaidEur?: number | null;
  };
  event_count_at_generation: number;
  model: string;
  created_at: string;
  stale: boolean;
};

export const summaryQueryKey = (caseId: string) => ['cases', caseId, 'summary'] as const;

export function useSummary(caseId: string | undefined) {
  const me = useMe();
  const qc = useQueryClient();
  const lastEventCountRef = useRef<number | null>(null);

  const query = useQuery({
    queryKey: summaryQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ summary: CaseSummary | null }>(`/cases/${caseId}/summary`),
    select: (d) => d.summary,
    enabled: !!caseId && !!me.data?.user,
    // Server-side `ensureCaseSummary` is idempotent for unchanged event
    // counts, so a quick refetch is cheap. Don't hammer it though.
    staleTime: 30_000,
  });

  // Server-side summary generation can update cases.title (auto-titling
  // the first time we have enough data). When the event count moves —
  // i.e. a fresh generation just ran — invalidate the case query so the
  // header picks up the new title.
  const eventCount = query.data?.event_count_at_generation ?? null;
  useEffect(() => {
    if (!caseId) return;
    if (eventCount === null) return;
    if (lastEventCountRef.current !== eventCount) {
      lastEventCountRef.current = eventCount;
      qc.invalidateQueries({ queryKey: caseQueryKey(caseId) });
      qc.invalidateQueries({ queryKey: casesQueryKey });
    }
  }, [caseId, eventCount, qc]);

  return query;
}
